import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

const SPIN_MS = 4600;
const EXTRA_TURNS = 5;
const EASE = [0.12, 0.78, 0.16, 1];
const MIN_TICK_MS = 45;

const bezier = (u, a, b) => {
    const v = 1 - u;
    return 3 * v * v * u * a + 3 * v * u * u * b + u * u * u;
};

const easeAt = (t) => {
    let lo = 0;
    let hi = 1;
    for (let i = 0; i < 14; i++) {
        const mid = (lo + hi) / 2;
        if (bezier(mid, EASE[0], EASE[2]) < t) lo = mid; else hi = mid;
    }
    return bezier((lo + hi) / 2, EASE[1], EASE[3]);
};

const PALETTE = [
    '#ff5fd2', '#6df35e', '#ffd24a', '#4dc9ff', '#ff8a4d',
    '#b06bff', '#4cb63a', '#ff6b8a', '#3ddce0', '#e8e34a',
];

const polar = (cx, cy, r, angleDeg) => {
    const rad = ((angleDeg - 90) * Math.PI) / 180;
    return [cx + r * Math.cos(rad), cy + r * Math.sin(rad)];
};

const segmentPath = (cx, cy, r, start, end) => {
    const [sx, sy] = polar(cx, cy, r, start);
    const [ex, ey] = polar(cx, cy, r, end);
    const large = end - start > 180 ? 1 : 0;
    return `M ${cx} ${cy} L ${sx} ${sy} A ${r} ${r} 0 ${large} 1 ${ex} ${ey} Z`;
};

const luminance = (hex) => {
    const h = String(hex || '').replace('#', '');
    if (h.length !== 6) return 0.5;
    const c = [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16) / 255)
        .map(v => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
    return 0.2126 * c[0] + 0.7152 * c[1] + 0.0722 * c[2];
};

const shorten = (text, max) => {
    const t = String(text || '');
    return t.length > max ? `${t.slice(0, max - 1)}…` : t;
};

// Labels sit horizontally, so a line's *width* is bounded by the chord across the
// segment and the lines stack inwards along the radius. Wrapping keeps each line
// inside the chord and the stack clear of the art band.
const wrapLabel = (text, maxChars, maxLines) => {
    const words = String(text || '').split(/\s+/).filter(Boolean);
    if (words.length === 0 || maxLines < 1) return [];

    const lines = [];
    let current = '';
    for (const word of words) {
        const next = current ? `${current} ${word}` : word;
        if (next.length <= maxChars || !current) {
            current = next;
            continue;
        }
        lines.push(current);
        current = word;
        if (lines.length === maxLines) {
            current = '';
            break;
        }
    }
    if (current && lines.length < maxLines) lines.push(current);

    const consumed = lines.join(' ').split(/\s+/).filter(Boolean).length;
    const truncated = consumed < words.length;

    return lines.map((line, i) => {
        const isLast = i === lines.length - 1;
        return shorten(isLast && truncated ? `${line}…` : line, maxChars);
    });
};

const clamp = (n, lo, hi) => Math.max(lo, Math.min(hi, n));

const RAD = Math.PI / 180;

export default function PrizeWheel({ segments, landedIndex, spinning, size = 300, onSettled, onTick }) {
    const [rotation, setRotation] = useState(0);
    const [artNatural, setArtNatural] = useState({});
    const settledRef = useRef(false);

    const artUrls = segments.map(s => s.image || '').filter(Boolean).join('|');

    useEffect(() => {
        if (!artUrls) return undefined;
        let active = true;
        for (const url of new Set(artUrls.split('|'))) {
            const img = new Image();
            img.onload = () => {
                if (!active || !img.naturalWidth || !img.naturalHeight) return;
                setArtNatural(prev => (
                    prev[url] ? prev : { ...prev, [url]: { w: img.naturalWidth, h: img.naturalHeight } }
                ));
            };
            img.src = url;
        }
        return () => { active = false; };
    }, [artUrls]);

    const count = segments.length;
    const evenSweep = count > 0 ? 360 / count : 360;
    const radius = size / 2;

    // Segment size follows the weighting, but on a square root curve with a floor
    // under it. A rare prize reads as clearly the smallest slice without collapsing
    // into an unreadable sliver, and the odds themselves are untouched: the winner
    // is already picked server side and the wheel only rotates to it.
    const layout = useMemo(() => {
        if (count === 0) return [];

        const curved = segments.map(segment => Math.sqrt(Math.max(0, Number(segment.weight) || 0)));
        const curvedTotal = curved.reduce((a, b) => a + b, 0);
        if (curvedTotal <= 0) {
            return segments.map((_, i) => ({ start: i * evenSweep, sweep: evenSweep }));
        }

        const floor = Math.min(evenSweep * 0.5, 20);
        const share = 360 - floor * count;

        let start = 0;
        return curved.map(value => {
            const own = floor + (share * value) / curvedTotal;
            const entry = { start, sweep: own };
            start += own;
            return entry;
        });
    }, [segments, count, evenSweep]);

    const colours = useMemo(() => {
        const distinct = new Set(segments.map(s => (s.colour || '').toLowerCase()).filter(Boolean));
        const useOwn = distinct.size >= 2;
        return segments.map((s, i) => (useOwn && s.colour ? s.colour : PALETTE[i % PALETTE.length]));
    }, [segments]);

    const targetRotation = useMemo(() => {
        if (landedIndex === null || landedIndex === undefined || count === 0) return null;
        const slice = layout[landedIndex];
        if (!slice) return null;
        const centre = slice.start + slice.sweep / 2;
        return EXTRA_TURNS * 360 + (360 - centre);
    }, [landedIndex, layout, count]);

    const finish = useCallback(() => {
        if (settledRef.current) return;
        settledRef.current = true;
        if (onSettled) onSettled();
    }, [onSettled]);

    useEffect(() => {
        if (targetRotation === null) return undefined;
        settledRef.current = false;
        const raf = requestAnimationFrame(() => setRotation(targetRotation));
        const timer = setTimeout(finish, SPIN_MS + 150);
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(timer);
        };
    }, [targetRotation, finish]);

    useEffect(() => {
        if (targetRotation === null || !onTick || evenSweep <= 0) return undefined;
        const begun = performance.now();
        let frame = 0;
        let passed = 0;
        let lastTick = 0;
        const step = (now) => {
            const elapsed = now - begun;
            if (elapsed >= SPIN_MS) return;
            const crossed = Math.floor((easeAt(elapsed / SPIN_MS) * targetRotation) / evenSweep);
            if (crossed > passed) {
                passed = crossed;
                if (now - lastTick >= MIN_TICK_MS) {
                    lastTick = now;
                    onTick();
                }
            }
            frame = requestAnimationFrame(step);
        };
        frame = requestAnimationFrame(step);
        return () => cancelAnimationFrame(frame);
    }, [targetRotation, evenSweep, onTick]);

    if (count === 0) {
        return (
            <div style={{
                width: size, height: size, display: 'flex', alignItems: 'center',
                justifyContent: 'center', color: 'rgba(255,255,255,0.5)', textAlign: 'center', padding: 20,
            }}>
                The wheel has no prizes on it yet.
            </div>
        );
    }

    const baseFont = Math.max(9, Math.min(15, (size / 22) - (count > 10 ? 2 : 0)));
    const hubRadius = size * 0.085;

    // Everything is rotated by `mid`, so the segment that stops under the pointer
    // ends up perfectly upright. In that frame local x runs across the segment and
    // local y runs inwards along the radius. The art keeps its own outer band so it
    // can never reach the label or the rim.
    const artCentre = radius * 0.74;
    const artRadial = Math.floor(radius * 0.28);
    const artInner = artCentre - artRadial / 2;
    const artAcrossFor = (segSweep) => Math.floor(2 * artCentre * Math.sin((segSweep / 2) * RAD)) - 8;

    // Every label sits at the same radius so they form a ring, and a segment with
    // no art does not drift out to the rim just because nothing is above it. When
    // the whole wheel is art free the block is centred in the segment instead.
    const anyArt = segments.some(segment => segment.image);
    const textOuter = (anyArt ? artInner : radius - 6) - 4;
    const textBand = Math.max(20, textOuter - hubRadius - 4);
    const blockCentre = anyArt
        ? textOuter - (3 * (baseFont + 1)) / 2
        : hubRadius + 4 + textBand / 2;

    // Pixel art only ever scales by a whole number, never fractionally. A sprite
    // too big for the band stays at 1x and the band crops it, so nothing is ever
    // squashed and nothing reaches the label or the next segment.
    const artFor = (segment, artAcross) => {
        if (!segment.image) return null;
        const natural = artNatural[segment.image];
        if (!natural || artAcross < 8 || artRadial < 8) return null;
        const scale = clamp(Math.floor(Math.min(artAcross / natural.w, artRadial / natural.h)), 1, 3);
        return { w: natural.w * scale, h: natural.h * scale };
    };

    return (
        <div style={{ position: 'relative', width: size, height: size }}>
            <div
                style={{
                    position: 'absolute', left: '50%', top: -2, transform: 'translateX(-50%)',
                    width: 0, height: 0, zIndex: 3,
                    borderLeft: `${size * 0.038}px solid transparent`,
                    borderRight: `${size * 0.038}px solid transparent`,
                    borderTop: `${size * 0.075}px solid #ffffff`,
                    filter: 'drop-shadow(0 2px 5px rgba(0,0,0,0.85))',
                }}
            />
            <svg
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
                style={{
                    display: 'block',
                    transform: `rotate(${rotation}deg)`,
                    transition: targetRotation === null ? 'none' : `transform ${SPIN_MS}ms cubic-bezier(0.12, 0.78, 0.16, 1)`,
                    filter: 'drop-shadow(0 6px 22px rgba(0,0,0,0.7))',
                }}
            >
                <circle cx={radius} cy={radius} r={radius - 2} fill="#0d0b1a" stroke="#ffffff" strokeWidth="3" />
                {segments.map((segment, i) => {
                    const slice = layout[i] || { start: i * evenSweep, sweep: evenSweep };
                    const segSweep = slice.sweep;
                    const start = slice.start;
                    const end = start + segSweep;
                    const mid = start + segSweep / 2;
                    const colour = colours[i];
                    const dim = segment.soldOut;
                    const textFill = luminance(colour) > 0.45 ? '#0d0b1a' : '#ffffff';
                    const artAcross = artAcrossFor(segSweep);
                    const art = artFor(segment, artAcross);
                    const [ax, ay] = polar(radius, radius, artCentre, mid);
                    const quantity = Number(segment.quantity) || 1;

                    const [tx, ty] = polar(radius, radius, blockCentre, mid);

                    // Lines stack inwards, so the radial band decides how many fit, and the
                    // slice's own width decides how long each one can be. A manual prize
                    // gives up a label line to make room for its quantity.
                    const chordAtText = Math.max(20, 2 * blockCentre * Math.sin((segSweep / 2) * RAD) - 6);
                    const maxChars = clamp(Math.floor(chordAtText / (baseFont * 0.4)), 4, 24);
                    const lineSlots = clamp(Math.floor(textBand / (baseFont + 1)), 1, 4);
                    const maxLines = clamp(segment.manual ? lineSlots - 1 : lineSlots, 1, 3);

                    // Quantity only means something on the staff delivered prizes; "1x" on
                    // a framps or cosmetic segment is noise.
                    const labelLines = wrapLabel(segment.label, maxChars, maxLines);
                    const lines = segment.manual ? [...labelLines, `${quantity}x`] : labelLines;
                    const fontSize = Math.floor(clamp(
                        Math.min(baseFont, Math.floor(textBand / Math.max(1, lines.length)) - 1),
                        8,
                        15
                    ));
                    const lineHeight = fontSize + 1;
                    return (
                        <g key={segment.id || i}>
                            <path
                                d={segmentPath(radius, radius, radius - 5, start, end)}
                                fill={colour}
                                opacity={dim ? 0.25 : 0.95}
                                stroke="#0d0b1a"
                                strokeWidth="1.5"
                            />
                            <text
                                x={tx}
                                y={ty}
                                fill={textFill}
                                stroke={textFill === '#ffffff' ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.5)'}
                                strokeWidth="2.5"
                                paintOrder="stroke"
                                strokeLinejoin="round"
                                fontSize={fontSize}
                                fontFamily="'Jersey 10', monospace"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                transform={`rotate(${mid} ${tx} ${ty})`}
                                style={{ pointerEvents: 'none', opacity: dim ? 0.5 : 1, letterSpacing: '0.03em' }}
                            >
                                {lines.map((line, li) => (
                                    <tspan
                                        key={li}
                                        x={tx}
                                        dy={li === 0 ? -((lines.length - 1) * lineHeight) / 2 : lineHeight}
                                    >
                                        {line}
                                    </tspan>
                                ))}
                            </text>
                            {art && (
                                <g transform={`rotate(${mid} ${ax} ${ay})`}>
                                    <clipPath id={`wheelart-${segment.id || i}`} clipPathUnits="userSpaceOnUse">
                                        <rect
                                            x={Math.round(ax - artAcross / 2)}
                                            y={Math.round(ay - artRadial / 2)}
                                            width={artAcross}
                                            height={artRadial}
                                        />
                                    </clipPath>
                                    <g clipPath={`url(#wheelart-${segment.id || i})`}>
                                        <image
                                            href={segment.image}
                                            x={Math.round(ax - art.w / 2)}
                                            y={Math.round(ay - art.h / 2)}
                                            width={art.w}
                                            height={art.h}
                                            preserveAspectRatio="none"
                                            style={{
                                                pointerEvents: 'none',
                                                imageRendering: 'pixelated',
                                                opacity: dim ? 0.4 : 1,
                                            }}
                                        />
                                    </g>
                                </g>
                            )}
                        </g>
                    );
                })}
                <circle cx={radius} cy={radius} r={size * 0.085} fill="#0d0b1a" stroke="#ffffff" strokeWidth="2.5" />
                <circle cx={radius} cy={radius} r={size * 0.03} fill="#ffffff" opacity={spinning ? 1 : 0.65} />
            </svg>
        </div>
    );
}
